import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth, db } from '../../../shared/storage/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { User } from '../../../types';
import { StudentAuthService } from '../../../shared/storage/StudentAuthService';
import { sessionTracker } from '../../../shared/services/SessionTracker';
import { ParentLoginTracker } from '../../parent/services/ParentLoginTracker';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  setStudentSession: (studentData: User, firebaseUser: FirebaseUser) => void;
  updateUserData: (updatedData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const updateUserData = (updatedData: Partial<User>) => {
    setUserData(prevData => {
      if (!prevData) return null;
      const newData = { ...prevData, ...updatedData };
      // If muses are being updated, merge them carefully
      if (updatedData.muses && prevData.muses) {
        newData.muses = [...prevData.muses, ...updatedData.muses];
      }
      return newData;
    });
  };

  const setStudentSession = (studentData: User, firebaseUser: FirebaseUser) => {
    setCurrentUser(firebaseUser);
    setUserData(studentData);
    sessionTracker.startSession(studentData.id);
    localStorage.setItem('lastActiveUserId', studentData.id);
    setLoading(false);

    // Attach a real-time listener for student users as well
    try {
      const userRef = doc(db, 'users', studentData.id);
      const unsubscribeStudent = onSnapshot(userRef, (snap) => {
        if (snap.exists()) {
          const liveData = { id: snap.id, ...snap.data() } as User;
          setUserData(liveData);
        }
      });
      // Store unsubscribe on window to avoid memory leak if re-login occurs
      // and ensure cleanup happens on unmount/auth change
      (window as any).__overtureStudentUserUnsub?.();
      (window as any).__overtureStudentUserUnsub = unsubscribeStudent;
    } catch (e) {
      console.error('Failed to attach student onSnapshot listener', e);
    }
  };

  useEffect(() => {
    let unsubscribeSnapshot: (() => void) | null = null;
    
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      // Clean up any existing listeners
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = null;
      }
      
      if (user && user.isAnonymous) {
        // This case is now handled by the Login component calling setStudentSession.
        // We can keep the user in the currentUser state, but we wait for the
        // explicit session call to set the full userData.
        setCurrentUser(user);
        
        // We need to check if we can restore a session from localStorage,
        // which would happen on a page refresh.
        const localInfo = StudentAuthService.getLocalStudentInfo();
        if (localInfo && localInfo.anonymousUid === user.uid) {
          try {
            const studentData = await StudentAuthService.getCurrentStudentData();
            if (studentData) {
              setStudentSession(studentData, user);
            } else {
              setLoading(false);
            }
          } catch (error) {
            console.error('Error restoring student session from localStorage:', error);
            setLoading(false);
          }
        }
        
        return;
      }
      
      setCurrentUser(user);
      setUserData(null); // Reset user data when auth state changes
      
      if (user) {
        // Restore any saved session time only AFTER Firebase auth is ready.
        // If we run this before auth resolves, the write hits Firestore unauthenticated
        // and we get permission-denied.
        void sessionTracker.checkForSessionBackup(user.uid);

        // Regular Firebase user (teacher)
        const userRef = doc(db, 'users', user.uid);
        unsubscribeSnapshot = onSnapshot(userRef, async (snap) => {
          if (snap.exists()) {
            const userData = { id: snap.id, ...snap.data() } as User;
            setUserData(userData);

            // Start appropriate session tracking based on user role
            if (userData.role === 'parent') {
              // Track parent login with detailed analytics
              // Don't block auth hydration on analytics writes (can permission-deny under strict rules)
              void ParentLoginTracker.recordLogin(user.uid).catch((e) => {
                console.warn('[AuthContext] Parent login tracking failed (non-fatal).', e);
              });
              ParentLoginTracker.setupAutoLogout(user.uid);
            } else {
              // Standard session tracking for teachers and other users
              sessionTracker.startSession(user.uid);
            }

            localStorage.setItem('lastActiveUserId', user.uid);
          } else {
            setUserData(null);
          }
          setLoading(false);
        });
      } else {
        // No user signed in - end any active session
        const lastUserId = localStorage.getItem('lastActiveUserId');
        if (lastUserId) {
          // Check if this was a parent session and handle appropriately
          const lastUserData = userData; // Store reference before clearing
          if (lastUserData?.role === 'parent') {
            await ParentLoginTracker.recordLogout(lastUserId);
          } else if (sessionTracker.isSessionActive()) {
            sessionTracker.endSession(lastUserId);
          }
          localStorage.removeItem('lastActiveUserId');
        }
        setUserData(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
      if ((window as any).__overtureStudentUserUnsub) {
        (window as any).__overtureStudentUserUnsub();
        (window as any).__overtureStudentUserUnsub = undefined;
      }
    };
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    setStudentSession,
    updateUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 